import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { AlertCircle, Eye, TrendingUp, Zap, BarChart3 } from 'lucide-react';

const SkinProfileSummary = () => {
  const [sliderProgress, setSliderProgress] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setSliderProgress(85), 500);
    return () => clearTimeout(timer);
  }, []);

  const infoCards = [
    { icon: <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500" />, label: 'Invloed op het uiterlijk:', value: 'Duidelijk zichtbaar', bg: 'bg-orange-50' },
    { icon: <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />, label: 'Verbeterpotentieel', value: 'Hoog', bg: 'bg-green-50' },
    { icon: <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />, label: 'Huidelasticiteit', value: 'Laag', bg: 'bg-red-50' },
    { icon: <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500" />, label: 'Collageen niveau', value: 'Ondergemiddeld', bg: 'bg-purple-50' },
  ];

  return (
    <div className="space-y-4 sm:space-y-5 max-w-lg mx-auto">
      <h1 className="text-lg sm:text-xl md:text-2xl font-extrabold text-gray-900 text-center">
        Samenvatting van jouw huidprofiel
      </h1>

      {/* Main card */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-5 pt-4 sm:pt-5">
          <h2 className="text-base sm:text-lg font-bold text-gray-900">Collageenverlies</h2>
          <span className="px-2.5 sm:px-3 py-1 rounded-full bg-red-50 text-red-500 text-xs sm:text-sm font-semibold border border-red-100">
            Hoog
          </span>
        </div>

        {/* Photo */}
        <div className="flex justify-center py-4 sm:py-5">
          <div className="w-32 h-40 sm:w-40 sm:h-48 rounded-xl overflow-hidden border-2 border-gray-100 bg-gray-50">
            <img
              src="https://cdn.shopify.com/s/files/1/1078/0940/7317/files/quiz-skin-profile-woman.png?v=1789292084&width=600"
              alt="Gepersonaliseerd huidprofiel overzicht"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Slider */}
        <div className="px-4 sm:px-5 pb-2">
          <div className="relative mb-1">
            {/* Tooltip */}
            <motion.div
              initial={{ opacity: 0, left: '0%' }}
              animate={{ opacity: 1, left: `${sliderProgress}%` }}
              transition={{ duration: 1.2, ease: 'easeOut', delay: 0.3 }}
              className="absolute -top-9 sm:-top-10 -translate-x-1/2"
            >
              <div className="bg-gray-900 text-white text-[10px] sm:text-xs font-semibold px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-lg whitespace-nowrap">
                Jouw niveau
              </div>
              <div className="w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] sm:border-l-[6px] sm:border-r-[6px] sm:border-t-[6px] border-l-transparent border-r-transparent border-t-gray-900 mx-auto" />
            </motion.div>

            {/* Track */}
            <div className="h-2.5 sm:h-3 rounded-full overflow-hidden flex">
              <div className="flex-1 bg-blue-400 rounded-l-full" />
              <div className="flex-1 bg-green-400" />
              <div className="flex-1 bg-yellow-400" />
              <div className="flex-1 bg-orange-400" />
              <div className="flex-1 bg-red-400 rounded-r-full" />
            </div>

            {/* Circle indicator */}
            <motion.div
              initial={{ left: '0%' }}
              animate={{ left: `${sliderProgress}%` }}
              transition={{ duration: 1.2, ease: 'easeOut', delay: 0.3 }}
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-white border-2 border-gray-400 shadow-md"
              style={{ top: '50%', marginTop: '-4px' }}
            />
          </div>

          {/* Labels */}
          <div className="flex justify-between text-[10px] sm:text-xs text-gray-400 mt-2">
            <span>Laag</span>
            <span>Normaal</span>
            <span>Gemiddeld</span>
            <span>Hoog</span>
          </div>
        </div>

        {/* Warning box */}
        <div className="mx-4 sm:mx-5 mb-4 sm:mb-5 mt-3 bg-red-50 border border-red-100 rounded-xl p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-1">
            <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
            <span className="text-[10px] sm:text-xs font-bold text-red-500 uppercase tracking-wide">
              Hoog collageenverlies
            </span>
          </div>
          <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
            Een verhoogd collageenverlies leidt tot diepe rimpels, slappe huid en een ongelijkmatige huidskleur. Het goede nieuws? Dit is behandelbaar met de juiste aanpak.
          </p>
        </div>
      </div>

      {/* Info grid */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3">
        {infoCards.map((card, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 + i * 0.15, duration: 0.4 }}
            className="bg-white rounded-xl border border-gray-100 p-3 sm:p-4 flex items-start gap-2 sm:gap-3"
          >
            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg ${card.bg} flex items-center justify-center shrink-0`}>
              {card.icon}
            </div>
            <div className="min-w-0">
              <p className="text-[10px] sm:text-xs text-gray-400 leading-tight">{card.label}</p>
              <p className="text-xs sm:text-sm font-bold text-gray-900">{card.value}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default SkinProfileSummary;